import { useMemo } from 'react';
import { startOfWeek, endOfWeek, eachDayOfInterval, format, isSameDay, isToday as checkIsToday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { AppointmentListItem, DoctorAvailabilityException } from '@/api';
import { TimeGridRuler } from './TimeGridRuler';
import { DayColumn } from './DayColumn';
import { cn } from '@/utils/cn';

export interface MobileDayCalendarProps {
  weekStart: Date;
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
  appointments: AppointmentListItem[];
  exceptions: DoctorAvailabilityException[];
  isLoading?: boolean;
  startHour?: number;
  endHour?: number;
  hourHeight?: number;
  onSelectAppointment: (appointment: AppointmentListItem) => void;
  onBlockSlot?: (date: Date, startTime: string, endTime: string) => void;
  onUnblockSlot?: (exceptionId: string) => void;
}

export function MobileDayCalendar({
  weekStart,
  selectedDate,
  onSelectDate,
  appointments,
  exceptions,
  isLoading = false,
  startHour = 7,
  endHour = 22,
  hourHeight = 72,
  onSelectAppointment,
  onBlockSlot,
  onUnblockSlot,
}: MobileDayCalendarProps) {
  // Gera os 7 dias da semana
  const days = useMemo(() => {
    const monday = startOfWeek(weekStart, { weekStartsOn: 1 });
    const sunday = endOfWeek(weekStart, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: monday, end: sunday });
  }, [weekStart]);

  // Contagem de consultas por dia
  const appointmentCountByDay = useMemo(() => {
    const countMap: Record<string, number> = {};
    for (const apt of appointments) {
      const dayKey = format(new Date(apt.scheduledAt), 'yyyy-MM-dd');
      countMap[dayKey] = (countMap[dayKey] || 0) + 1;
    }
    return countMap;
  }, [appointments]);

  const selectedDateFormatted = format(selectedDate, "EEEE, d 'de' MMMM", { locale: ptBR });
  const isSelectedToday = checkIsToday(selectedDate);

  return (
    <div className="flex flex-col rounded-xl border border-border bg-card shadow-xs overflow-hidden">
      {/* Seletor horizontal de dias (Carrossel / Pílulas da Semana) */}
      <div className="border-b border-border bg-muted/20 p-2">
        <div className="grid grid-cols-7 gap-1">
          {days.map((day) => {
            const isSelected = isSameDay(day, selectedDate);
            const isDayToday = checkIsToday(day);
            const dayKey = format(day, 'yyyy-MM-dd');
            const aptCount = appointmentCountByDay[dayKey] || 0;
            const dayOfWeekStr = format(day, 'EEE', { locale: ptBR }).replace('.', '').slice(0, 3);
            const dayOfMonthStr = format(day, 'd');

            return (
              <button
                key={day.toISOString()}
                type="button"
                onClick={() => onSelectDate(day)}
                className={cn(
                  'flex flex-col items-center justify-center py-2 px-1 rounded-lg transition-all text-center relative',
                  isSelected
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'hover:bg-muted text-muted-foreground hover:text-foreground',
                  !isSelected && isDayToday && 'border border-primary/40 font-semibold'
                )}
              >
                <span className="text-[10px] uppercase font-semibold tracking-tight block">
                  {dayOfWeekStr}
                </span>
                <span className="text-sm font-bold block mt-0.5">
                  {dayOfMonthStr}
                </span>

                {/* Badge com contagem de consultas se houver */}
                {aptCount > 0 && (
                  <span
                    className={cn(
                      'mt-1 text-[9px] px-1.5 py-0.2 rounded-full font-semibold',
                      isSelected
                        ? 'bg-primary-foreground text-primary'
                        : 'bg-primary/20 text-primary'
                    )}
                  >
                    {aptCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Rótulo do dia selecionado */}
        <div className="mt-2.5 px-1 flex items-center justify-between text-xs">
          <span className="font-semibold text-foreground capitalize">
            {selectedDateFormatted}
          </span>
          {isSelectedToday && (
            <span className="text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
              Hoje
            </span>
          )}
        </div>
      </div>

      {/* Grade diária com régua e coluna única */}
      <div className="relative flex max-h-[640px] overflow-y-auto scrollbar-thin">
        {/* Loading overlay sutil */}
        {isLoading && (
          <div className="absolute inset-0 bg-background/50 backdrop-blur-[1px] z-40 flex items-center justify-center">
            <div className="h-4 w-4 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          </div>
        )}

        {/* Régua de horas */}
        <TimeGridRuler
          startHour={startHour}
          endHour={endHour}
          hourHeight={hourHeight}
        />

        {/* Coluna única do dia selecionado ocupando toda a largura restante */}
        <div className="flex-1">
          <DayColumn
            date={selectedDate}
            isToday={isSelectedToday}
            appointments={appointments}
            exceptions={exceptions}
            startHour={startHour}
            endHour={endHour}
            hourHeight={hourHeight}
            onSelectAppointment={onSelectAppointment}
            onBlockSlot={onBlockSlot}
            onUnblockSlot={onUnblockSlot}
          />
        </div>
      </div>
    </div>
  );
}
